import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { buildService, buildBreadcrumb } from "@/lib/seo";
import { Check, Filter, Inbox, ShieldCheck } from "lucide-react";

const FEATURES = [
  { icon: Filter, t: "Filter by city, county, situation", d: "Get only the deals that match your buy box." },
  { icon: Inbox, t: "Direct seller messaging", d: "Reach motivated sellers in-app — no middlemen." },
  { icon: ShieldCheck, t: "Verified ADN trust badge", d: "Upload proof of funds and stand out." },
];

export default function ForInvestors() {
  return (
    <div>
      <Seo
        title="For Investors & Cash Buyers — Pre-Qualified Motivated Seller Leads"
        description="Stop chasing cold lists. ADN brings you verified distressed sellers actively seeking offers, filterable by city, county, situation, and price. Plans from $97/month."
        path="/for-investors"
        jsonLd={[
          buildService(
            "Distressed-property investor lead access",
            "Pre-qualified motivated-seller leads with city/county/situation filtering, in-app messaging, and offer submission tools.",
            null,
            {
              "@type": "AggregateOffer",
              priceCurrency: "USD",
              lowPrice: "97",
              highPrice: "997",
              offerCount: "3",
            }
          ),
          buildBreadcrumb([
            { name: "Home", url: "https://adn-marketplace.com/" },
            { name: "For Investors", url: "https://adn-marketplace.com/for-investors" },
          ]),
        ]}
      />
      <Navbar />
      <section className="bg-[var(--adn-navy)] text-white grain">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <p className="eyebrow text-[var(--adn-gold)]">For investors & cash buyers</p>
          <h1 className="font-display text-5xl sm:text-6xl font-light mt-3 max-w-3xl">Real motivated sellers. <span className="text-[var(--adn-gold)]">Real situations.</span></h1>
          <p className="mt-6 text-white/75 max-w-2xl text-lg leading-relaxed">
            Stop chasing cold lists. ADN brings you sellers who have already raised their hand and shared their situation — and lets you submit cash, creative, or hybrid offers in seconds.
          </p>
          <div className="mt-10 flex gap-3">
            <Link to="/login" data-testid="fi-join" className="btn-gold px-7 py-4 rounded text-sm">Join As Investor</Link>
            <Link to="/pricing" className="btn-outline-light px-7 py-4 rounded text-sm">See Pricing</Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid md:grid-cols-3 gap-6">
        {FEATURES.map((f, i) => (
          <div key={i} className="bg-white border border-[var(--adn-line)] p-7">
            <f.icon size={22} className="text-[var(--adn-gold)]" />
            <h3 className="font-display text-xl font-semibold mt-4">{f.t}</h3>
            <p className="mt-2 text-[var(--adn-text-soft)] leading-relaxed">{f.d}</p>
          </div>
        ))}
      </section>

      <section className="bg-white border-y border-[var(--adn-line)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
          <p className="eyebrow">Offer types we support</p>
          <h2 className="font-display text-4xl font-medium mt-3">Submit the right offer for the situation.</h2>
          <ul className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3 text-[15px]">
            {["Cash Offer","Subject-To","Seller Finance","Novation","Listing Proposal","Refinance Option","Loan Modification Referral","Contractor Repair Estimate","Legal Help Referral","Short Sale Option"].map((s) => (
              <li key={s} className="flex items-center gap-2"><Check size={16} className="text-[var(--adn-emerald)]" />{s}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 py-20">
        <p className="eyebrow">How investors win on ADN</p>
        <h2 className="font-display text-4xl font-medium mt-3 mb-8">Cold lists vs. ADN leads — what changes.</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-[var(--adn-line)] p-7">
            <p className="font-display text-lg font-semibold">Pre-qualified motivation</p>
            <p className="mt-2 text-[var(--adn-text-soft)] leading-relaxed">Every seller submitted their own property and described their situation in detail. You are not interrupting someone with a postcard — they are actively comparing solutions.</p>
          </div>
          <div className="bg-white border border-[var(--adn-line)] p-7">
            <p className="font-display text-lg font-semibold">Real-time AI summary</p>
            <p className="mt-2 text-[var(--adn-text-soft)] leading-relaxed">Each lead arrives with an AI-generated distress score (0-100), estimated equity, and recommended solution paths so you can underwrite in seconds.</p>
          </div>
          <div className="bg-white border border-[var(--adn-line)] p-7">
            <p className="font-display text-lg font-semibold">In-app messaging + walkthroughs</p>
            <p className="mt-2 text-[var(--adn-text-soft)] leading-relaxed">Talk directly with the seller without sharing personal contact info. Schedule walkthroughs in the same dashboard. Everything stays organized.</p>
          </div>
          <div className="bg-white border border-[var(--adn-line)] p-7">
            <p className="font-display text-lg font-semibold">Verified badge</p>
            <p className="mt-2 text-[var(--adn-text-soft)] leading-relaxed">Upload proof of funds and ADN reviews your account. Verified investors get priority and a visible trust badge on every offer.</p>
          </div>
        </div>
      </section>

      <section className="bg-white border-t border-[var(--adn-line)]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-20">
          <p className="eyebrow">Frequently asked</p>
          <h2 className="font-display text-4xl font-medium mt-3 mb-10">Investor FAQ.</h2>
          <div className="space-y-7">
            {[
              { q: "How many leads can I expect per month?", a: "Volume depends on your selected cities and your competitiveness. Pro and City Partner plans typically see significantly more deal flow than Starter due to alerts and priority routing. There is no minimum lead guarantee in MVP; we recommend pairing your subscription with a clear buy box." },
              { q: "Do I have to be a licensed agent?", a: "No. Investors do not need a real-estate license. However, you must follow your state's laws around offers, assignments, and disclosures. ADN does not provide legal advice." },
              { q: "Can I cancel anytime?", a: "Yes. Subscriptions are month to month with no long-term contract. Cancel from your profile at any time. Once billing is activated on the platform, you keep access through the end of the billing cycle you have already paid for." },
              { q: "What types of offers convert best?", a: "It depends on the situation. Foreclosure and tax-delinquent sellers often need speed — cash offers convert well. Probate and inherited properties often respond to listing proposals or hybrid options. The marketplace lets you submit whatever fits." },
            ].map((f, i) => (
              <details key={i} className="border-b border-[var(--adn-line)] pb-5 group">
                <summary className="font-display text-lg font-semibold cursor-pointer list-none flex items-start justify-between gap-4">
                  <span>{f.q}</span>
                  <span className="text-[var(--adn-gold)] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[var(--adn-text-soft)] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
